import {Button, Separator} from 'platform/components';
import {Box, Clickable, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {useResolveVehicleWarningsMutation} from '../../../store/importApi';
import {handleApiError} from '../../../utils/handleApiError';
import {FeatureListRow} from '../../AdvertisementPage/components/FeatureListRow';
import {ImportVehicle} from '../types/ImportVehicle';
import {attributeToRow} from '../utils/attributeToRow';
import {translateAttributeLabel} from '../utils/translateAttributeLabel';
import {
  ImportVehicleListRowFrame,
  ImportVehicleListRowIndicator,
} from './ImportVehicleListRowFrame';
import {ImportVehicleSummary} from './ImportVehicleSummary/ImportVehicleSummary';

interface VehicleImportRowProps extends TestIdProps {
  batchId: string;
  vehicle: ImportVehicle;
  isExpanded: boolean;
  onToggle: () => void;
  onEditSpecification: () => void;
  onEditEquipment: () => void;
}

export function VehicleImportRow(props: VehicleImportRowProps) {
  const [resolveVehicleWarnings, {isLoading: isResolveLoading}] =
    useResolveVehicleWarningsMutation();

  return (
    <ImportVehicleListRowFrame
      $rowStatus={props.vehicle.rowStatus}
      data-testid={suffixTestId('row', props)}
    >
      <ImportVehicleListRowIndicator $rowStatus={props.vehicle.rowStatus} />
      <Clickable onClick={props.onToggle} data-testid={suffixTestId('rowHeader', props)}>
        <Box paddingLeft={4} paddingRight={4} paddingTop={2} paddingBottom={2}>
          <HStack justify="space-between" align="center">
            <ImportVehicleSummary
              vehicle={props.vehicle}
              data-testid={suffixTestId('vehicleSummary', props)}
            />
            <Icon
              value={props.isExpanded ? 'navigation/expand_less' : 'navigation/expand_more'}
              size={6}
              color="text.secondary"
            />
          </HStack>
        </Box>
      </Clickable>

      {props.isExpanded && (
        <Box paddingLeft={4} paddingRight={4} paddingBottom={4}>
          <VStack spacing={4}>
            {props.vehicle.features.length > 0 && (
              <FeatureListRow
                values={props.vehicle.features}
                spacing={2}
                textColor="secondary"
                data-testid={suffixTestId('featuresRow', props)}
              />
            )}

            <HStack spacing={3}>
              <Button
                variant="secondary"
                leftIcon="automotive/car"
                title={i18n.t('page.import.actions.editSpecification')}
                onClick={props.onEditSpecification}
                data-testid={suffixTestId('editSpecButton', props)}
              />
              <Button
                variant="secondary"
                leftIcon="content/create"
                title={i18n.t('page.import.actions.editEquipment')}
                onClick={props.onEditEquipment}
                data-testid={suffixTestId('editEquipmentButton', props)}
              />
              <Show when={props.vehicle.unresolvedWarnings}>
                <Button
                  variant="warning"
                  leftIcon="action/check_circle"
                  title={i18n.t('page.import.actions.confirmSpecificationOk')}
                  isLoading={isResolveLoading}
                  onClick={() =>
                    resolveVehicleWarnings({
                      batchId: props.batchId,
                      vehicleId: props.vehicle.id,
                    })
                      .unwrap()
                      .catch(handleApiError)
                  }
                  data-testid={suffixTestId('confirmSpecOkButton', props)}
                />
              </Show>
            </HStack>

            {props.vehicle.attributes.length > 0 && (
              <VStack spacing={2}>
                <Separator />
                <Text size="xSmall" color="secondary">
                  {i18n.t('page.import.labels.originalValues')}
                </Text>
                <AttributesColumnFlow data-testid={suffixTestId('attributes', props)}>
                  {props.vehicle.attributes.map((attr) => {
                    const row = attributeToRow(attr, translateAttributeLabel);
                    return (
                      <Box
                        key={`${attr.label}\0${attr.value}`}
                        paddingVertical={2}
                        borderColor="palettes.neutral.30.100"
                        borderBottom="1px solid"
                      >
                        <HStack spacing={4}>
                          <Box width="50%">
                            <Text size="xSmall" color="secondary">
                              {row.label}
                            </Text>
                          </Box>
                          <Box flex={1} minWidth={0}>
                            {row.content}
                          </Box>
                        </HStack>
                      </Box>
                    );
                  })}
                </AttributesColumnFlow>
              </VStack>
            )}
          </VStack>
        </Box>
      )}
    </ImportVehicleListRowFrame>
  );
}

const AttributesColumnFlow = styled.div`
  column-count: 3;
  column-gap: ${({theme}) => theme.getSize(4)};
  width: 100%;

  @media ${({theme}) => theme.devices.mobile} {
    column-count: 1;
  }

  @media ${({theme}) => theme.devices.tablet} {
    column-count: 2;
  }
`;
